import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as S from './style';

interface GaugeChartProps {
    media_geral: number; // 0 to 100
    media_solidos?: number;
    media_liquidos?: number;
    media_sementes?: number;
    is_empty: boolean;
    size?: number;
}

export const GaugeChart: React.FC<GaugeChartProps> = ({ media_geral, media_solidos, media_liquidos, media_sementes, is_empty, size }) => {
    const radius = 100;
    const stroke = 40;
    const cx = 150; // Centered in 300 width
    const cy = 125;  // Adjusted for height

    // Visually balanced section angles (symmetric around 270°).
    // Extreme ranges compressed, small ranges expanded for visibility.
    // s1: 36° | s2: 26° | s3: 20° | s4: 16° | s5: 20° | s6: 26° | s7: 36°

    const valueToAngle = (val: number) => {
        const v = Math.min(Math.max(val, 0), 150);
        if (v < 80) return 180 + (v / 80) * 36;
        else if (v < 95) return 216 + ((v - 80) / 15) * 26;
        else if (v < 99) return 242 + ((v - 95) / 4) * 20;
        else if (v < 101) return 262 + ((v - 99) / 2) * 16;
        else if (v < 105) return 278 + ((v - 101) / 4) * 20;
        else if (v < 120) return 298 + ((v - 105) / 15) * 26;
        else return 324 + ((v - 120) / 30) * 36;
    };

    const sections = useMemo(() => [
        { color: "#dc143c", start: 180, end: 216, label: "0%", tooltip: "Muito abaixo", id: "s1", offsetX: 15, offsetY: 0 },
        { color: "#ffd700", start: 216, end: 242, label: "80%", tooltip: "Abaixo", id: "s2", offsetX: 0, offsetY: 0 },
        { color: "#5da57d", start: 242, end: 262, label: "95%", tooltip: "Abaixo permitido", id: "s3", offsetX: 0, offsetY: 5 },
        { color: "#7ebd61", start: 262, end: 278, label: "100%", tooltip: "Preciso", id: "s4", offsetX: 0, offsetY: 5 },
        { color: "#5da57d", start: 278, end: 298, label: "105%", tooltip: "Acima permitido", id: "s5", offsetX: 0, offsetY: 5 },
        { color: "#ffd700", start: 298, end: 324, label: "120%", tooltip: "Acima", id: "s6", offsetX: 0, offsetY: 0 },
        { color: "#dc143c", start: 324, end: 360, label: "150%", tooltip: "Muito acima", id: "s7", offsetX: -15, offsetY: 0 },
    ], []);

    const needleAngle = valueToAngle(media_geral);

    const [animatedAngle, setAnimatedAngle] = useState(180);
    const [displayValue, setDisplayValue] = useState(0);
    const rafRef = useRef<number>(0);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setAnimatedAngle(needleAngle);

            const duration = 1000;
            const start = performance.now();
            const from = 0;
            const to = media_geral;

            const animate = (now: number) => {
                const elapsed = now - start;
                const progress = Math.min(elapsed / duration, 1);
                // ease-out
                const eased = 1 - Math.pow(1 - progress, 3);
                setDisplayValue(Math.round(from + (to - from) * eased));
                if (progress < 1) {
                    rafRef.current = requestAnimationFrame(animate);
                }
            };

            rafRef.current = requestAnimationFrame(animate);
        }, 100);

        return () => {
            clearTimeout(timeout);
            cancelAnimationFrame(rafRef.current);
        };
    }, [needleAngle, media_geral]);

    const needleLength = radius - 10;
    const needleTip = {
        x: cx + needleLength * Math.cos(needleAngle * Math.PI / 180),
        y: cy + needleLength * Math.sin(needleAngle * Math.PI / 180)
    };

    const polarToCartesian = (angle: number, distance: number) => {
        const rad = angle * Math.PI / 180;
        return {
            x: cx + distance * Math.cos(rad),
            y: cy + distance * Math.sin(rad)
        };
    };

    const labelRadius = radius + stroke / 2 + 18;

    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

    return (
        <S.Container style={size ? { maxWidth: size } : undefined}>
            <svg viewBox="-10 0 320 215">
                {/* Sections with labels - always visible */}
                {sections.map((section, index) => {
                    const startRad = section.start * Math.PI / 180;
                    const endRad = section.end * Math.PI / 180;

                    const x1 = cx + radius * Math.cos(startRad);
                    const y1 = cy + radius * Math.sin(startRad);

                    const x2 = cx + radius * Math.cos(endRad);
                    const y2 = cy + radius * Math.sin(endRad);

                    // Label positioning: first at start, last at end, others at Cartesian midpoint
                    const isFirst = index === 0;
                    const isLast = index === sections.length - 1;
                    let labelPos: { x: number; y: number };
                    if (isFirst) {
                        labelPos = polarToCartesian(section.start, labelRadius);
                    } else if (isLast) {
                        labelPos = polarToCartesian(section.end, labelRadius);
                    } else {
                        const p1 = polarToCartesian(section.start, labelRadius);
                        const p2 = polarToCartesian(section.end, labelRadius);
                        labelPos = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
                    }

                    // Apply manual offsets
                    labelPos.x += section.offsetX;
                    labelPos.y += section.offsetY;

                    // Determine text-anchor
                    let anchor: string = "middle";
                    if (isFirst) anchor = "end";
                    else if (isLast) anchor = "start";

                    const isHovered = hoveredIndex === index;
                    const hasHover = hoveredIndex !== null;

                    return (
                        <React.Fragment key={index + 'gauge' + section.color}>
                            <path
                                d={`M ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2}`}
                                fill="none"
                                stroke={is_empty ? '#f5f5f5' : section.color}
                                strokeWidth={!is_empty && isHovered ? stroke + 8 : stroke}
                                style={{
                                    // cursor: is_empty ? 'default' : 'pointer',
                                    opacity: !is_empty && hasHover && !isHovered ? 0.4 : 1,
                                    transition: 'stroke-width 0.2s ease, opacity 0.2s ease',
                                }}
                                onMouseEnter={() => !is_empty && setHoveredIndex(index)}
                                onMouseMove={(e) => {
                                    if (is_empty) return;
                                    const rect = (e.currentTarget.closest('svg') as SVGSVGElement)?.parentElement?.getBoundingClientRect();
                                    if (rect) {
                                        setTooltipPos({
                                            x: e.clientX - rect.left,
                                            y: e.clientY - rect.top
                                        });
                                    }
                                }}
                                onMouseLeave={() => !is_empty && setHoveredIndex(null)}
                            />
                            <text
                                x={labelPos.x}
                                y={labelPos.y}
                                textAnchor={anchor}
                                alignmentBaseline="middle"
                                fontSize="11"
                                fill="#555"
                                fontWeight="600"
                            >
                                {section.label}
                            </text>
                        </React.Fragment>
                    );
                })}

                {/* Rounded Caps - always visible */}
                <circle
                    cx={cx + radius * Math.cos(sections[0].start * Math.PI / 180)}
                    cy={cy + radius * Math.sin(sections[0].start * Math.PI / 180)}
                    r={stroke / 2}
                    fill={is_empty ? '#f5f5f5' : sections[0].color}
                    pointerEvents="none"
                />
                <circle
                    cx={cx + radius * Math.cos(sections[sections.length - 1].end * Math.PI / 180)}
                    cy={cy + radius * Math.sin(sections[sections.length - 1].end * Math.PI / 180)}
                    r={stroke / 2}
                    fill={is_empty ? '#f5f5f5' : sections[sections.length - 1].color}
                    pointerEvents="none"
                />

                {/* Needle - always visible */}
                <g
                    style={{
                        transform: `rotate(${animatedAngle}deg)`,
                        transformOrigin: `${cx}px ${cy}px`,
                        transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    <path
                        d={`M ${cx + radius - 30} ${cy - 5} L ${cx + radius + 30} ${cy - 2} L ${cx + radius + 30} ${cy + 2} L ${cx + radius - 30} ${cy + 5} Z`}
                        fill="#111"
                        stroke="#fff"
                        strokeWidth="2.5"
                        strokeLinejoin="round"
                    />
                </g>

                {/* Percentage value and sub-labels - hidden when empty */}
                {!is_empty && (
                    <>
                        <text x={cx} y={cy} textAnchor="middle" fontSize="35" fontWeight="800" fill="var(--color-text)">
                            {displayValue}%
                        </text>

                        <text x={cx} y={cy + 65} textAnchor="middle" fontSize="13" fill="#888" fontWeight="500">
                            <tspan x={cx - 110} textDecoration={media_solidos === null ? 'line-through' : undefined}>Sólidos: </tspan><tspan fontWeight="700" textDecoration={media_solidos === null ? 'line-through' : undefined}>{Math.round(media_solidos ?? 0)}%</tspan>
                            <tspan x={cx} textDecoration={media_liquidos === null ? 'line-through' : undefined}>Líquidos: </tspan><tspan fontWeight="700" textDecoration={media_liquidos === null ? 'line-through' : undefined}>{Math.round(media_liquidos ?? 0)}%</tspan>
                            <tspan x={cx + 110} textDecoration={media_sementes === null ? 'line-through' : undefined}>Sementes: </tspan><tspan fontWeight="700" textDecoration={media_sementes === null ? 'line-through' : undefined}>{Math.round(media_sementes ?? 0)}%</tspan>
                        </text>
                    </>
                )}
            </svg>

            {hoveredIndex !== null && (
                <div
                    style={{
                        position: 'absolute',
                        left: tooltipPos.x,
                        top: tooltipPos.y,
                        transform: 'translate(15px, 15px)',
                        background: 'rgba(0, 0, 0, 0.8)',
                        padding: 7,
                        color: '#fff',
                        fontWeight: 500,
                        borderRadius: 5,
                        fontSize: 13,
                        zIndex: 200,
                        pointerEvents: 'none',
                    }}
                >
                    <span style={{ margin: 0, whiteSpace: 'nowrap' }}>
                        {sections[hoveredIndex].tooltip}
                    </span>
                </div>
            )}
        </S.Container>
    );
};
